import { clone } from 'three/addons/utils/SkeletonUtils.js';

/** Three finite echoes retain the actual authored mesh and recorded local bone
 * transforms. They are visual history only: no entity, hit plane, independent
 * clock, geometry copy or shared-material mutation is introduced. */
export function createSkyHourglassEchoes(T, gltf) {
  const instances = new Map();
  let disposed = false;
  function createEcho(index) {
    const group = new T.Group(),
      source = clone(gltf.scene),
      nodes = [],
      materials = new Set(),
      skeletons = new Set();
    group.name = `hourglass-history-${index}`;
    group.add(source);
    source.traverse((node) => {
      nodes.push(node);
      if (!node.isMesh) return;
      if (node.isSkinnedMesh) skeletons.add(node.skeleton);
      const copy = (original) => {
        const material = original.clone();
        material.transparent = true;
        material.opacity = 0;
        material.depthWrite = false;
        material.color?.lerp(new T.Color(index % 2 ? '#ca944c' : '#62bfc9'), 0.45);
        material.emissive?.set(index % 2 ? '#443014' : '#133b43');
        material.emissiveIntensity = 0.4;
        materials.add(material);
        return material;
      };
      node.material = Array.isArray(node.material) ? node.material.map(copy) : copy(node.material);
      node.castShadow = false;
      node.receiveShadow = false;
      node.raycast = () => {};
    });
    return { group, nodes, materials, skeletons };
  }
  function release(host) {
    const echoes = instances.get(host);
    if (!echoes) return;
    instances.delete(host);
    for (const echo of echoes) {
      echo.group.removeFromParent();
      for (const material of echo.materials) material.dispose();
      for (const skeleton of echo.skeletons) skeleton.dispose();
    }
  }
  return {
    update(host, item, reduced = false) {
      if (disposed) return;
      let echoes = instances.get(host);
      if (!echoes) {
        echoes = Array.from({ length: 3 }, (_, index) => createEcho(index));
        instances.set(host, echoes);
      }
      const frames = item?.hourglass?.echoes ?? [];
      for (let i = 0; i < echoes.length; i++) {
        const echo = echoes[i],
          frame = frames[i];
        echo.group.visible = Boolean(
          host.visible && host.parent && frame && !item.defeated && !reduced,
        );
        if (echo.group.parent !== host.parent) {
          echo.group.removeFromParent();
          host.parent?.add(echo.group);
        }
        if (!echo.group.visible) continue;
        echo.group.position.fromArray(frame.rootTransform.position);
        echo.group.quaternion.fromArray(frame.rootTransform.quaternion);
        echo.group.scale.fromArray(frame.rootTransform.scale);
        for (let n = 0; n < echo.nodes.length; n++) {
          const pose = frame.nodes[n];
          if (!pose) continue;
          echo.nodes[n].position.fromArray(pose.position);
          echo.nodes[n].quaternion.fromArray(pose.quaternion);
          echo.nodes[n].scale.fromArray(pose.scale);
        }
        for (const material of echo.materials) material.opacity = frame.opacity;
        echo.group.updateMatrixWorld(true);
      }
    },
    release,
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const host of instances.keys()) release(host);
    },
  };
}
